import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer,
} from "recharts";
import {
  Plus, Pencil, Trash2, X, Eye, BarChart3, Copy, Pause, Play, Archive,
  Check, Download, Upload, Search, Lock, CreditCard, RotateCcw, Send, Info,
  Image as ImageIcon, ArrowLeft, ArchiveRestore, Megaphone,
  Link2,
} from "lucide-react";
import { api, apiError, mediaUrl, downloadReport } from "@/lib/api";

export const PINK = "hsl(330, 82%, 55%)";
const AMBER = "hsl(45, 93%, 55%)";
const GREEN = "hsl(142, 70%, 45%)";

export const Inp = (p) => <input {...p} className={`w-full bg-background border border-border rounded-xl px-4 py-2.5 outline-none focus:border-primary ${p.className || ""}`} />;
export const Txt = (p) => <textarea {...p} className={`w-full bg-background border border-border rounded-xl px-4 py-2.5 outline-none focus:border-primary ${p.className || ""}`} />;
export const Sel = (p) => <select {...p} className={`w-full bg-background border border-border rounded-xl px-4 py-2.5 outline-none focus:border-primary ${p.className || ""}`} />;

const STATUS_STYLES = {
  active: "text-primary border-primary/40", approved: "text-primary border-primary/40",
  pending: "text-amber-400 border-amber-400/40", paused: "text-amber-400 border-amber-400/40",
  rejected: "text-destructive border-destructive/40", draft: "text-muted-foreground border-border",
  completed: "text-muted-foreground border-border", archived: "text-muted-foreground border-border",
  changes_requested: "text-amber-400 border-amber-400/40", expired: "text-muted-foreground border-border",
};

export function StatusPill({ s }) {
  return <span data-testid={`status-pill-${s}`} className={`px-2.5 py-0.5 rounded-full border text-[11px] font-semibold capitalize whitespace-nowrap ${STATUS_STYLES[s] || "text-muted-foreground border-border"}`}>{(s || "draft").replace(/_/g, " ")}</span>;
}

export function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className={`w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto bg-card border border-border rounded-2xl p-6`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-display font-bold">{title}</h3>
          <button onClick={onClose} data-testid="modal-close" className="p-1 hover:text-primary"><X className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const RANGES = [["today", "Today"], ["yesterday", "Yesterday"], ["7d", "Last 7 Days"], ["30d", "Last 30 Days"], ["month", "This Month"], ["last_month", "Last Month"], ["all", "All Time"], ["custom", "Custom"]];

export function DateFilter({ range, setRange, start, setStart, end, setEnd }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="date-filter">
      {RANGES.map(([v, l]) => (
        <button key={v} onClick={() => setRange(v)} data-testid={`range-${v}`}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${range === v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>{l}</button>
      ))}
      {range === "custom" && (
        <span className="flex items-center gap-2">
          <Inp type="date" value={start} onChange={(e) => setStart(e.target.value)} className="!w-40" data-testid="range-start" />
          <span className="text-muted-foreground text-xs">to</span>
          <Inp type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="!w-40" data-testid="range-end" />
        </span>
      )}
    </div>
  );
}

export function MetricsGrid({ m, extra = [], quartiles = false }) {
  const cards = [
    ...extra,
    ["Total Impressions", m.impressions], ["Unique Impressions", m.unique_impressions],
    ["Ad Starts", m.starts],
    ...(quartiles ? [["25% Views", m.views_25], ["50% Views", m.views_50], ["75% Views", m.views_75]] : []),
    ["Completed Views", m.completed_views], ["Completion Rate", m.completion_rate + "%"],
    ["Clicks", m.clicks], ["CTR", m.ctr + "%"], ["Chapter Unlocks", m.unlocks],
    ["Unique Viewers", m.unique_viewers], ["Avg Watch Time", m.avg_watch_time + "s"],
    ...(quartiles ? [["Avg Completion", m.avg_completion + "%"]] : []),
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="metrics-grid">
      {cards.map(([l, v]) => (
        <div key={l} className="rounded-2xl border border-border surface-2 p-4">
          <p className="track-label text-[10px] text-muted-foreground">{l}</p>
          <p className="text-2xl font-display font-bold mt-1">{v ?? 0}</p>
        </div>
      ))}
    </div>
  );
}

const TOOLTIP_STYLE = { background: "hsl(0 0% 8%)", border: "1px solid hsl(0 0% 20%)", borderRadius: 12, fontSize: 12 };

export function SeriesChart({ data }) {
  if (!data?.length) return <p className="text-sm text-muted-foreground text-center py-8">No activity in this range yet.</p>;
  return (
    <div className="rounded-2xl border border-border surface-2 p-4" data-testid="series-chart">
      <p className="track-label text-xs text-muted-foreground mb-3">Performance over time</p>
      <ResponsiveContainer width="100%" height={240}>
        <AreaChart data={data} margin={{ left: -20, right: 8, top: 4 }}>
          <CartesianGrid stroke="hsl(0 0% 100% / 0.06)" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(0 0% 60%)" }} tickFormatter={(d) => d.slice(5)} />
          <YAxis tick={{ fontSize: 10, fill: "hsl(0 0% 60%)" }} />
          <RTooltip contentStyle={TOOLTIP_STYLE} />
          <Area type="monotone" dataKey="impressions" stroke={PINK} fill={PINK} fillOpacity={0.15} strokeWidth={2} name="Impressions" />
          <Area type="monotone" dataKey="completed" stroke={GREEN} fill={GREEN} fillOpacity={0.1} strokeWidth={2} name="Completed" />
          <Area type="monotone" dataKey="clicks" stroke={AMBER} fill={AMBER} fillOpacity={0.1} strokeWidth={2} name="Clicks" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function BreakdownBars({ rows, nameKey = "name" }) {
  if (!rows?.length) return null;
  const data = rows.map((r) => ({ ...r, name: (r[nameKey] || "").slice(0, 18) }));
  return (
    <div className="rounded-2xl border border-border surface-2 p-4" data-testid="breakdown-chart">
      <p className="track-label text-xs text-muted-foreground mb-3">Breakdown</p>
      <ResponsiveContainer width="100%" height={Math.max(160, rows.length * 46)}>
        <BarChart data={data} layout="vertical" margin={{ left: 20, right: 16 }}>
          <CartesianGrid stroke="hsl(0 0% 100% / 0.06)" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 10, fill: "hsl(0 0% 60%)" }} />
          <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "hsl(0 0% 75%)" }} />
          <RTooltip contentStyle={TOOLTIP_STYLE} />
          <Bar dataKey="impressions" fill={PINK} radius={[0, 6, 6, 0]} name="Impressions" />
          <Bar dataKey="clicks" fill={AMBER} radius={[0, 6, 6, 0]} name="Clicks" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ReportButtons({ level, id, range, start, end }) {
  const dl = async (fmt) => {
    try {
      await downloadReport(`/adx/reports/${level}/${id}?format=${fmt}&range=${range}&start=${start || ""}&end=${end || ""}`, `${level}-analytics-${range}.${fmt}`);
      toast.success(`${fmt.toUpperCase()} report downloaded`);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex gap-2 flex-wrap" data-testid={`download-${level}-analytics`}>
      {["csv", "xlsx", "pdf"].map((f) => (
        <button key={f} onClick={() => dl(f)} data-testid={`download-${f}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-xs font-semibold hover:border-primary transition-colors">
          <Download className="h-3.5 w-3.5" /> {f.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export function LockedPanel({ reason, onPay, paying, fee }) {
  return (
    <div className="rounded-2xl border border-border surface-2 p-8 text-center" data-testid="analytics-locked">
      <Lock className="h-10 w-10 text-primary mx-auto" />
      <h3 className="text-xl font-display font-bold mt-3">Analytics Locked</h3>
      {reason === "payment_required" ? (
        <>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">Complete your one-time advertiser activation payment{fee ? ` ($${fee})` : ""} to unlock full campaign & ad analytics.</p>
          {onPay && (
            <button onClick={onPay} disabled={paying} data-testid="pay-activation-btn" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold disabled:opacity-50">
              <CreditCard className="h-4 w-4" /> {paying ? "Redirecting…" : "Pay & Activate"}
            </button>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">Payment received. Your account is awaiting admin approval — analytics unlock once an admin activates your advertiser account.</p>
      )}
    </div>
  );
}

export function MediaThumb({ url, type, className }) {
  const [err, setErr] = useState(false);
  if (!url || err)
    return <div className={`grid place-items-center bg-secondary/60 text-muted-foreground text-[10px] ${className}`} data-testid="media-unavailable">Media unavailable</div>;
  return type === "video"
    ? <video src={mediaUrl(url)} className={`object-cover ${className}`} onError={() => setErr(true)} />
    : <img src={mediaUrl(url)} alt="" className={`object-cover ${className}`} onError={() => setErr(true)} />;
}

export function MediaLibrary({ picker = false, pickerType = "all", onPick }) {
  const [tab, setTab] = useState(pickerType === "video" ? "video" : pickerType === "image" ? "image" : "all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("newest");
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/adx/media", { params: { type: tab, q, sort } }).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [tab, q, sort]);
  useEffect(() => { load(); }, [load]);
  const act = async (a, action) => {
    if ((action === "delete" || action === "archive") && !window.confirm(`${action === "delete" ? "Delete" : "Archive"} "${a.name}"?`)) return;
    try { await api.post(`/adx/media/${a.id}/action`, { action }); toast.success("Updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const up = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded to library"); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {[["all", "All"], ["image", "Images"], ["video", "Videos"]].map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`media-tab-${t}`} className={`px-4 py-1.5 rounded-full text-xs font-semibold border ${tab === t ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{l}</button>
        ))}
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Inp value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search media…" className="!pl-9" data-testid="media-search" />
        </div>
        <Sel value={sort} onChange={(e) => setSort(e.target.value)} className="!w-36" data-testid="media-sort">
          <option value="newest">Newest</option><option value="oldest">Oldest</option><option value="name">Name</option><option value="size">Size</option>
        </Sel>
        {!picker && (
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold cursor-pointer">
            <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload"}
            <input type="file" accept="image/*,video/*" onChange={up} className="hidden" data-testid="media-library-upload" />
          </label>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {rows.map((a) => (
          <div key={a.id} className="rounded-2xl border border-border surface-2 overflow-hidden" data-testid={`media-asset-${a.id}`}>
            <MediaThumb url={a.url} type={a.type} className="w-full h-28" />
            <div className="p-3">
              <p className="text-xs font-medium truncate" title={a.name}>{a.name}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5 capitalize">{a.type} · {(a.size / 1024).toFixed(0)}KB · {(a.created_at || "").slice(0, 10)}</p>
              <p className="text-[10px] text-muted-foreground">Used by {a.used_by} ad{a.used_by === 1 ? "" : "s"}{a.archived ? " · archived" : ""}</p>
              <div className="flex gap-1 mt-2 flex-wrap">
                <button onClick={() => setPreview(a)} title="Preview" data-testid="media-preview-btn" className="p-1.5 rounded border border-border hover:border-primary"><Eye className="h-3.5 w-3.5" /></button>
                {picker ? (
                  <button onClick={() => onPick(a)} data-testid="media-use-btn" className="px-2 py-1 rounded border border-primary text-primary text-[11px] font-semibold">Use in Ad</button>
                ) : (
                  <>
                    <a href={mediaUrl(a.url)} download={a.name} title="Download" data-testid="media-download-btn" className="p-1.5 rounded border border-border hover:border-primary inline-flex"><Download className="h-3.5 w-3.5" /></a>
                    {a.archived
                      ? <button onClick={() => act(a, "restore")} title="Restore" data-testid="media-restore-btn" className="p-1.5 rounded border border-border hover:border-primary"><ArchiveRestore className="h-3.5 w-3.5" /></button>
                      : <button onClick={() => act(a, "archive")} title="Archive" data-testid="media-archive-btn" className="p-1.5 rounded border border-border hover:border-primary"><Archive className="h-3.5 w-3.5" /></button>}
                    <button onClick={() => act(a, "delete")} title="Delete" data-testid="media-delete-btn" className="p-1.5 rounded border border-border hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {!rows.length && <p className="text-muted-foreground text-center py-10 text-sm">No media found. Upload an image or video to get started.</p>}
      {preview && (
        <Modal title={preview.name} onClose={() => setPreview(null)}>
          {preview.type === "video"
            ? <video src={mediaUrl(preview.url)} controls autoPlay className="w-full rounded-xl border border-border" />
            : <img src={mediaUrl(preview.url)} alt="" className="w-full rounded-xl border border-border" />}
        </Modal>
      )}
    </div>
  );
}

function MediaPickerModal({ type, onPick, onClose }) {
  return (
    <Modal title="Choose from Media Library" onClose={onClose} wide>
      <MediaLibrary picker pickerType={type} onPick={onPick} />
    </Modal>
  );
}

export function AdMediaField({ type, value, onChange, onMeta }) {
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(false);
  const [link, setLink] = useState("");
  const [fetching, setFetching] = useState(false);
  const fetchUrl = async () => {
    if (!/^https?:\/\//i.test(link.trim())) return toast.error("Paste a full https:// link");
    setFetching(true);
    try {
      const { data } = await api.post("/media/fetch-url", { url: link.trim(), type });
      onChange(data.url, data.type); onMeta?.(data.meta || {}); setLink("");
      toast.success(`${data.type === "video" ? "Video" : "Photo"} detected & saved${data.type !== type ? ` — switched to ${data.type === "video" ? "Video" : "Image"} Ad` : ""}`);
    } catch (err) { toast.error(apiError(err)); }
    setFetching(false);
  };
  const up = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url, data.type); toast.success("Uploaded");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2 flex-wrap">
        <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm cursor-pointer hover:border-primary transition-colors">
          <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload photo / video"}
          <input type="file" accept="image/*,video/*" onChange={up} className="hidden" data-testid="ad-media-upload" />
        </label>
        <button type="button" onClick={() => setPicker(true)} data-testid="choose-from-library" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm hover:border-primary transition-colors">
          <ImageIcon className="h-4 w-4" /> Choose from Media Library
        </button>
      </div>
      <div className="flex gap-2">
        <input value={link} onChange={(e) => setLink(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), fetchUrl())}
          placeholder="…or paste any photo / video link (auto-detected)" data-testid="ad-media-url-input"
          className="flex-1 min-w-0 bg-background border border-border rounded-full px-4 py-2 text-sm outline-none focus:border-primary" />
        <button type="button" onClick={fetchUrl} disabled={fetching || !link} data-testid="ad-media-url-fetch"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50">
          <Link2 className="h-4 w-4" /> {fetching ? "Fetching…" : "Fetch"}
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground">Only use media you own or have rights to. Streaming pages (YouTube, Instagram) aren't supported — upload the file instead.</p>
      {value && <MediaThumb url={value} type={type} className="w-full max-h-44 rounded-xl border border-border" />}
      {picker && <MediaPickerModal type={type} onClose={() => setPicker(false)} onPick={(a) => { onChange(a.url); setPicker(false); }} />}
    </div>
  );
}

export function AdPreviewCard({ ad }) {
  return (
    <div className="rounded-2xl border border-border overflow-hidden">
      <MediaThumb url={ad.media_url} type={ad.ad_type} className="w-full max-h-64" />
      <div className="p-4">
        <p className="text-xs text-muted-foreground">{ad.brand_name}</p>
        <h4 className="font-display font-bold">{ad.headline || ad.title}</h4>
        {ad.primary_text && <p className="text-sm mt-1">{ad.primary_text}</p>}
        {ad.description && <p className="text-sm text-muted-foreground mt-1">{ad.description}</p>}
        {ad.destination_url && <span className="inline-block mt-3 px-4 py-2 rounded-full border border-primary text-primary text-sm">{ad.cta_text || "Learn More"}</span>}
      </div>
    </div>
  );
}

export function AdPreviewModal({ ad, onClose }) {
  if (!ad) return null;
  return (
    <Modal title="Ad Preview" onClose={onClose}>
      <p className="track-label text-xs text-muted-foreground text-center mb-2">Advertisement — reader view</p>
      <AdPreviewCard ad={ad} />
    </Modal>
  );
}

export function AdDetailModal({ ad, campaign, onClose, onEdit }) {
  if (!ad) return null;
  const rows = [["Ad Name", ad.title], ["Campaign", campaign?.name || "—"], ["Advertiser", ad.advertiser_company || "—"],
    ["Type", ad.ad_type], ["Brand", ad.brand_name], ["Headline", ad.headline], ["Primary Text", ad.primary_text],
    ["Description", ad.description], ["CTA", ad.cta_text], ["Destination", ad.destination_url],
    ["Start", ad.start_date || "—"], ["End", ad.end_date || "—"], ["Priority", ad.priority],
    ["Placement", ad.placement], ["Frequency cap", ad.frequency || "—"]];
  return (
    <Modal title={`Ad — ${ad.title}`} onClose={onClose} wide>
      <div className="grid md:grid-cols-2 gap-4">
        <AdPreviewCard ad={ad} />
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center gap-2 mb-2"><StatusPill s={ad.status} />{ad.rejection_reason && <span className="text-xs text-destructive">{ad.rejection_reason}</span>}</div>
          {rows.map(([l, v]) => (
            <div key={l} className="flex gap-2"><span className="w-28 shrink-0 text-muted-foreground text-xs pt-0.5">{l}</span><span className="break-words min-w-0">{String(v ?? "—") || "—"}</span></div>
          ))}
          {onEdit && <button onClick={onEdit} data-testid="ad-detail-edit" className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"><Pencil className="h-4 w-4" /> Edit Ad</button>}
        </div>
      </div>
    </Modal>
  );
}

const EMPTY_AD = { title: "", brand_name: "", headline: "", primary_text: "", description: "", cta_text: "Learn More", destination_url: "", ad_type: "image", media_url: "", start_date: "", end_date: "", priority: 1, placement: "chapter_unlock", frequency: 0, tracking: {} };

export function AdForm({ campaign, ad, onClose, onSaved }) {
  const [f, setF] = useState(ad ? { ...EMPTY_AD, ...ad, tracking: ad.tracking || {} } : EMPTY_AD);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (submit) => {
    if (!f.title) return toast.error("Ad name is required");
    if (!f.media_url) return toast.error("Creative (image/video) is required");
    setSaving(true);
    try {
      const payload = { ...f, priority: parseInt(f.priority) || 1, frequency: parseInt(f.frequency) || 0, submit };
      if (ad) await api.put(`/adx/ads/${ad.id}`, payload);
      else await api.post(`/adx/campaigns/${campaign.id}/ads`, payload);
      toast.success(submit ? "Submitted for approval" : "Saved as draft");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };
  return (
    <Modal title={ad ? `Edit Ad — ${ad.title}` : "Create Ad"} onClose={onClose} wide>
      {!preview ? (
        <div className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div><p className="text-xs text-muted-foreground mb-1">Ad Name *</p><Inp value={f.title} onChange={set("title")} data-testid="ad-name" /></div>
            <div><p className="text-xs text-muted-foreground mb-1">Brand Name</p><Inp value={f.brand_name || ""} onChange={set("brand_name")} data-testid="ad-brand" /></div>
          </div>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div className="rounded-xl border border-border p-3"><p className="text-[10px] track-label text-muted-foreground">Campaign</p><p className="font-medium">{campaign?.name || "—"}</p></div>
            <div className="rounded-xl border border-border p-3"><p className="text-[10px] track-label text-muted-foreground">Advertiser</p><p className="font-medium">{campaign?.advertiser_company || ad?.advertiser_company || "—"}</p></div>
          </div>
          <Inp placeholder="Headline" value={f.headline || ""} onChange={set("headline")} data-testid="ad-headline" />
          <Inp placeholder="Primary text" value={f.primary_text || ""} onChange={set("primary_text")} />
          <Txt rows={2} placeholder="Description" value={f.description || ""} onChange={set("description")} />
          <div className="grid md:grid-cols-2 gap-3">
            <Inp placeholder="CTA text" value={f.cta_text || ""} onChange={set("cta_text")} />
            <Inp placeholder="Destination URL" value={f.destination_url || ""} onChange={set("destination_url")} data-testid="ad-dest-url" />
          </div>
          <div className="flex gap-2">{["image", "video"].map((t) => <button key={t} onClick={() => setF({ ...f, ad_type: t, media_url: "" })} data-testid={`ad-type-${t}`} className={`px-4 py-2 rounded-full text-sm border ${f.ad_type === t ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{t === "image" ? "Image Ad" : "Video Ad"}</button>)}</div>
          <AdMediaField type={f.ad_type} value={f.media_url} onChange={(url, t) => setF((p) => ({ ...p, media_url: url, ad_type: t || p.ad_type }))}
            onMeta={(m) => setF((p) => ({ ...p, title: p.title || m.title || "", description: p.description || m.description || "", ...("headline" in p ? { headline: p.headline || m.title || "" } : {}), destination_url: p.destination_url || m.destination_url || "" }))} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><p className="text-xs text-muted-foreground mb-1">Start</p><Inp type="date" value={f.start_date || ""} onChange={set("start_date")} /></div>
            <div><p className="text-xs text-muted-foreground mb-1">End</p><Inp type="date" value={f.end_date || ""} onChange={set("end_date")} /></div>
            <div><p className="text-xs text-muted-foreground mb-1">Priority</p><Inp type="number" value={f.priority} onChange={set("priority")} /></div>
            <div><p className="text-xs text-muted-foreground mb-1">Freq. cap/day</p><Inp type="number" value={f.frequency} onChange={set("frequency")} /></div>
          </div>
          <div><p className="text-xs text-muted-foreground mb-1">Placement</p>
            <Sel value={f.placement || "chapter_unlock"} onChange={set("placement")} data-testid="ad-placement">
              <option value="chapter_unlock">Chapter Unlock (rewarded)</option>
              <option value="interstitial">Interstitial</option>
              <option value="banner">Banner</option>
            </Sel></div>
          <details className="rounded-xl border border-border p-3">
            <summary className="text-xs font-semibold cursor-pointer">Tracking settings (optional)</summary>
            <div className="space-y-2 mt-2">
              <Inp placeholder="Impression pixel URL" value={f.tracking?.impression_pixel || ""} onChange={(e) => setF({ ...f, tracking: { ...f.tracking, impression_pixel: e.target.value } })} />
              <Inp placeholder="Click tracker URL" value={f.tracking?.click_tracker || ""} onChange={(e) => setF({ ...f, tracking: { ...f.tracking, click_tracker: e.target.value } })} />
            </div>
          </details>
          <div className="flex gap-2 pt-2 flex-wrap">
            <button onClick={() => setPreview(true)} data-testid="ad-preview-btn" className="px-5 py-2.5 rounded-full border border-border font-medium">Preview</button>
            <button onClick={() => save(false)} disabled={saving} data-testid="ad-save-draft" className="px-5 py-2.5 rounded-full border border-border font-medium">Save Draft</button>
            <button onClick={() => save(true)} disabled={saving} data-testid="ad-submit" className="flex-1 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">Submit for Approval</button>
          </div>
        </div>
      ) : (
        <div>
          <AdPreviewCard ad={f} />
          <button onClick={() => setPreview(false)} className="mt-4 px-5 py-2.5 rounded-full border border-border">← Back to edit</button>
        </div>
      )}
    </Modal>
  );
}

export function CampaignForm({ advertiserId, campaign, onClose, onSaved }) {
  const [f, setF] = useState(campaign
    ? { name: campaign.name, description: campaign.description || "", start_date: campaign.start_date || "", end_date: campaign.end_date || "" }
    : { name: "", description: "", start_date: "", end_date: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name) return toast.error("Campaign name required");
    setSaving(true);
    try {
      if (campaign) await api.put(`/adx/campaigns/${campaign.id}`, f);
      else await api.post("/adx/campaigns", { ...f, advertiser_id: advertiserId });
      toast.success(campaign ? "Campaign updated" : "Campaign created");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };
  return (
    <Modal title={campaign ? `Edit Campaign — ${campaign.name}` : "New Campaign"} onClose={onClose}>
      <div className="space-y-3">
        <Inp placeholder="Campaign name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="campaign-name" />
        <Txt rows={2} placeholder="Description (optional)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <div><p className="text-xs text-muted-foreground mb-1">Start</p><Inp type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></div>
          <div><p className="text-xs text-muted-foreground mb-1">End</p><Inp type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></div>
        </div>
        <button onClick={save} disabled={saving} data-testid="campaign-save" className="w-full px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">{campaign ? "Save Changes" : "Create Campaign"}</button>
      </div>
    </Modal>
  );
}

export function AdvertiserForm({ advertiser, onClose, onSaved }) {
  const isEdit = !!advertiser;
  const [f, setF] = useState(isEdit
    ? { company: advertiser.company || "", contact: advertiser.contact || "", email: advertiser.email || "", password: "", phone: advertiser.phone || "", website: advertiser.website || "", status: advertiser.status || "active", paid: !!advertiser.paid }
    : { company: "", contact: "", email: "", password: "", phone: "", website: "", status: "active", paid: false });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.company || !f.email) return toast.error("Company and email are required");
    if (!isEdit && !f.password) return toast.error("Password required for new advertiser");
    setSaving(true);
    try {
      if (isEdit) {
        const p = { ...f };
        if (!p.password) delete p.password;
        await api.put(`/adx/advertisers/${advertiser.id}`, p);
      } else {
        await api.post("/adx/advertisers", f);
      }
      toast.success(isEdit ? "Advertiser updated" : "Advertiser created");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };
  return (
    <Modal title={isEdit ? `Edit Advertiser — ${advertiser.company || advertiser.name}` : "New Advertiser"} onClose={onClose}>
      <div className="space-y-3">
        <Inp placeholder="Company / Brand name *" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="advertiser-company" />
        <Inp placeholder="Contact person" value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} />
        <Inp type="email" placeholder="Email *" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="advertiser-email" />
        <Inp type="password" placeholder={isEdit ? "New password (leave blank to keep)" : "Password *"} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="advertiser-password" />
        <div className="grid grid-cols-2 gap-3">
          <Inp placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          <Inp placeholder="Website" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3 items-end">
          <div><p className="text-xs text-muted-foreground mb-1">Status</p>
            <Sel value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="advertiser-status">
              <option value="pending">Pending</option><option value="active">Active</option>
              <option value="paused">Paused</option><option value="archived">Archived</option>
            </Sel></div>
          <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={f.paid} onChange={(e) => setF({ ...f, paid: e.target.checked })} data-testid="advertiser-paid" /> Activation paid</label>
        </div>
        <button onClick={save} disabled={saving} data-testid="advertiser-save" className="w-full px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">{isEdit ? "Save Changes" : "Create Advertiser"}</button>
      </div>
    </Modal>
  );
}

export async function entityAction(kind, id, action) {
  let reason;
  if (action === "reject" || action === "request_changes") reason = window.prompt("Reason (optional):") || "";
  if (action === "delete" && !window.confirm(`Delete this ${kind.slice(0, -1)}? This cannot be undone.`)) return false;
  if (action === "archive" && !window.confirm("Archive this item?")) return false;
  await api.post(`/adx/${kind}/${id}/action`, { action, reason });
  return true;
}

export function IconBtn({ title, onClick, testid, danger, children }) {
  return <button onClick={onClick} title={title} data-testid={testid} className={`p-1.5 rounded border border-border transition-colors ${danger ? "hover:text-destructive hover:border-destructive" : "hover:border-primary"}`}>{children}</button>;
}

export function CampaignRowActions({ c, isAdmin, act, onEdit, onAnalytics, onOpen }) {
  return (
    <div className="flex gap-1 flex-wrap items-center">
      <IconBtn title="View" testid="campaign-view" onClick={() => onOpen(c)}><Eye className="h-3.5 w-3.5" /></IconBtn>
      <IconBtn title="Edit" testid="campaign-edit" onClick={() => onEdit(c)}><Pencil className="h-3.5 w-3.5" /></IconBtn>
      <IconBtn title="Analytics" testid="campaign-analytics" onClick={() => onAnalytics(c)}><BarChart3 className="h-3.5 w-3.5" /></IconBtn>
      <IconBtn title="Duplicate" testid="campaign-duplicate" onClick={() => act("campaigns", c.id, "duplicate")}><Copy className="h-3.5 w-3.5" /></IconBtn>
      {c.status === "active" && <IconBtn title="Pause" testid="campaign-pause" onClick={() => act("campaigns", c.id, "pause")}><Pause className="h-3.5 w-3.5" /></IconBtn>}
      {(c.status === "paused" || c.status === "approved") && <IconBtn title="Resume" testid="campaign-resume" onClick={() => act("campaigns", c.id, "resume")}><Play className="h-3.5 w-3.5" /></IconBtn>}
      {isAdmin && c.status === "pending" && <IconBtn title="Approve" testid="campaign-approve" onClick={() => act("campaigns", c.id, "approve")}><Check className="h-3.5 w-3.5 text-primary" /></IconBtn>}
      {isAdmin && c.status === "approved" && <IconBtn title="Activate" testid="campaign-activate" onClick={() => act("campaigns", c.id, "activate")}><Play className="h-3.5 w-3.5 text-primary" /></IconBtn>}
      {isAdmin && c.status !== "rejected" && <IconBtn title="Reject" testid="campaign-reject" danger onClick={() => act("campaigns", c.id, "reject")}><X className="h-3.5 w-3.5" /></IconBtn>}
      <IconBtn title="Archive" testid="campaign-archive" onClick={() => act("campaigns", c.id, "archive")}><Archive className="h-3.5 w-3.5" /></IconBtn>
      <IconBtn title="Delete" testid="campaign-delete" danger onClick={() => act("campaigns", c.id, "delete")}><Trash2 className="h-3.5 w-3.5" /></IconBtn>
    </div>
  );
}

export function AdRowActions({ a, isAdmin, act, onEdit, onPreview, onAnalytics, onDetail }) {
  return (
    <div className="flex gap-1 flex-wrap items-center">
      <IconBtn title="View" testid="ad-view" onClick={() => onDetail(a)}><Info className="h-3.5 w-3.5" /></IconBtn>
      <IconBtn title="Edit" testid="ad-edit" onClick={() => onEdit(a)}><Pencil className="h-3.5 w-3.5" /></IconBtn>
      <IconBtn title="Preview" testid="ad-preview" onClick={() => onPreview(a)}><Eye className="h-3.5 w-3.5" /></IconBtn>
      <IconBtn title="Analytics" testid="ad-analytics" onClick={() => onAnalytics(a)}><BarChart3 className="h-3.5 w-3.5" /></IconBtn>
      <IconBtn title="Duplicate" testid="ad-duplicate" onClick={() => act("ads", a.id, "duplicate")}><Copy className="h-3.5 w-3.5" /></IconBtn>
      {a.status === "active" && <IconBtn title="Pause" testid="ad-pause" onClick={() => act("ads", a.id, "pause")}><Pause className="h-3.5 w-3.5" /></IconBtn>}
      {a.status === "paused" && <IconBtn title="Resume" testid="ad-resume" onClick={() => act("ads", a.id, "resume")}><Play className="h-3.5 w-3.5" /></IconBtn>}
      {["draft", "rejected", "changes_requested"].includes(a.status) && <IconBtn title="Submit for Approval" testid="ad-submit-approval" onClick={() => act("ads", a.id, "submit")}><Send className="h-3.5 w-3.5" /></IconBtn>}
      {isAdmin && ["pending", "changes_requested"].includes(a.status) && <IconBtn title="Approve" testid="ad-approve" onClick={() => act("ads", a.id, "approve")}><Check className="h-3.5 w-3.5 text-primary" /></IconBtn>}
      {isAdmin && a.status === "approved" && <IconBtn title="Activate" testid="ad-activate" onClick={() => act("ads", a.id, "activate")}><Play className="h-3.5 w-3.5 text-primary" /></IconBtn>}
      {isAdmin && a.status === "pending" && <IconBtn title="Request Changes" testid="ad-request-changes" onClick={() => act("ads", a.id, "request_changes")}><RotateCcw className="h-3.5 w-3.5" /></IconBtn>}
      {isAdmin && a.status !== "rejected" && <IconBtn title="Reject" testid="ad-reject" danger onClick={() => act("ads", a.id, "reject")}><X className="h-3.5 w-3.5" /></IconBtn>}
      <IconBtn title="Archive" testid="ad-archive" onClick={() => act("ads", a.id, "archive")}><Archive className="h-3.5 w-3.5" /></IconBtn>
      <IconBtn title="Delete" testid="ad-delete" danger onClick={() => act("ads", a.id, "delete")}><Trash2 className="h-3.5 w-3.5" /></IconBtn>
    </div>
  );
}

/* ---------- analytics blocks ---------- */
export function AdAnalyticsBlock({ id, lockProps = {} }) {
  const [range, setRange] = useState("30d");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/adx/ads/${id}/analytics`, { params: { range, start, end } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [id, range, start, end]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-muted-foreground py-8 text-center text-sm">Loading analytics…</p>;
  if (data.locked) return <LockedPanel reason={data.reason} {...lockProps} />;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <DateFilter range={range} setRange={setRange} start={start} setStart={setStart} end={end} setEnd={setEnd} />
        <ReportButtons level="ad" id={id} range={range} start={start} end={end} />
      </div>
      <MetricsGrid m={data.metrics} quartiles={data.ad?.ad_type === "video"} />
      <SeriesChart data={data.series} />
    </div>
  );
}

export function CampaignAnalyticsBlock({ id, lockProps = {} }) {
  const [range, setRange] = useState("30d");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/adx/campaigns/${id}/analytics`, { params: { range, start, end } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [id, range, start, end]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-muted-foreground py-8 text-center text-sm">Loading analytics…</p>;
  if (data.locked) return <LockedPanel reason={data.reason} {...lockProps} />;
  const bars = data.ads.map((a) => ({ name: a.title, impressions: a.metrics.impressions, clicks: a.metrics.clicks }));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <DateFilter range={range} setRange={setRange} start={start} setStart={setStart} end={end} setEnd={setEnd} />
        <ReportButtons level="campaign" id={id} range={range} start={start} end={end} />
      </div>
      <MetricsGrid m={data.metrics} extra={[["Total Ads", data.total_ads]]} />
      <SeriesChart data={data.series} />
      <BreakdownBars rows={bars} />
      {!!data.ads.length && (
        <div className="rounded-2xl border border-border surface-2 p-4 overflow-x-auto" data-testid="campaign-ads-performance">
          <p className="track-label text-xs text-muted-foreground mb-3">Ad Performance</p>
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
              {["Ad", "Type", "Status", "Impressions", "Completed", "Clicks", "CTR", "Completion"].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {data.ads.map((a) => (
                <tr key={a.id} className="border-b border-border/50" data-testid={`perf-ad-${a.id}`}>
                  <td className="py-2 pr-3 font-medium">{a.title}</td>
                  <td className="py-2 pr-3 capitalize">{a.ad_type}</td>
                  <td className="py-2 pr-3"><StatusPill s={a.status} /></td>
                  <td className="py-2 pr-3">{a.metrics.impressions}</td>
                  <td className="py-2 pr-3">{a.metrics.completed_views}</td>
                  <td className="py-2 pr-3">{a.metrics.clicks}</td>
                  <td className="py-2 pr-3">{a.metrics.ctr}%</td>
                  <td className="py-2 pr-3">{a.metrics.completion_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AdvertiserAnalyticsBlock({ id, lockProps = {} }) {
  const [range, setRange] = useState("30d");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/adx/advertisers/${id}/analytics`, { params: { range, start, end } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [id, range, start, end]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-muted-foreground py-8 text-center text-sm">Loading analytics…</p>;
  if (data.locked) return <LockedPanel reason={data.reason} {...lockProps} />;
  const bars = data.campaigns.map((c) => ({ name: c.name, impressions: c.metrics.impressions, clicks: c.metrics.clicks }));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <DateFilter range={range} setRange={setRange} start={start} setStart={setStart} end={end} setEnd={setEnd} />
        <ReportButtons level="advertiser" id={id} range={range} start={start} end={end} />
      </div>
      <MetricsGrid m={data.metrics} extra={[["Total Campaigns", data.totals.campaigns], ["Active Campaigns", data.totals.active_campaigns], ["Total Ads", data.totals.ads]]} />
      <SeriesChart data={data.series} />
      <BreakdownBars rows={bars} />
      {!!data.campaigns.length && (
        <div className="rounded-2xl border border-border surface-2 p-4 overflow-x-auto" data-testid="advertiser-campaign-performance">
          <p className="track-label text-xs text-muted-foreground mb-3">Campaign Performance</p>
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
              {["Campaign", "Status", "Ads", "Impressions", "Completed", "Clicks", "CTR", "Completion"].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {data.campaigns.map((c) => (
                <tr key={c.id} className="border-b border-border/50" data-testid={`perf-campaign-${c.id}`}>
                  <td className="py-2 pr-3 font-medium">{c.name}</td>
                  <td className="py-2 pr-3"><StatusPill s={c.status} /></td>
                  <td className="py-2 pr-3">{c.ad_count}</td>
                  <td className="py-2 pr-3">{c.metrics.impressions}</td>
                  <td className="py-2 pr-3">{c.metrics.completed_views}</td>
                  <td className="py-2 pr-3">{c.metrics.clicks}</td>
                  <td className="py-2 pr-3">{c.metrics.ctr}%</td>
                  <td className="py-2 pr-3">{c.metrics.completion_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!!data.ads.length && (
        <div className="rounded-2xl border border-border surface-2 p-4 overflow-x-auto" data-testid="advertiser-ad-performance">
          <p className="track-label text-xs text-muted-foreground mb-3">Ad Performance</p>
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
              {["Ad", "Type", "Status", "Impressions", "Completed", "Clicks", "CTR"].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {data.ads.map((a) => (
                <tr key={a.id} className="border-b border-border/50" data-testid={`perf-ad-${a.id}`}>
                  <td className="py-2 pr-3 font-medium">{a.title}</td>
                  <td className="py-2 pr-3 capitalize">{a.ad_type}</td>
                  <td className="py-2 pr-3"><StatusPill s={a.status} /></td>
                  <td className="py-2 pr-3">{a.metrics.impressions}</td>
                  <td className="py-2 pr-3">{a.metrics.completed_views}</td>
                  <td className="py-2 pr-3">{a.metrics.clicks}</td>
                  <td className="py-2 pr-3">{a.metrics.ctr}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------- campaign detail (shared admin/advertiser) ---------- */
export function CampaignDetailView({ cid, isAdmin, onBack, onOpenAdAnalytics }) {
  const [data, setData] = useState(null);
  const [editCamp, setEditCamp] = useState(false);
  const [adForm, setAdForm] = useState(null); // "new" | ad object
  const [previewAd, setPreviewAd] = useState(null);
  const [detailAd, setDetailAd] = useState(null);
  const load = useCallback(() => {
    api.get(`/adx/campaigns/${cid}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [cid]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-muted-foreground py-10 text-center text-sm">Loading campaign…</p>;
  const { campaign: c, advertiser: adv, ads } = data;
  const act = async (kind, id, action) => {
    try { if (await entityAction(kind, id, action)) { toast.success("Updated"); load(); } }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-6" data-testid="campaign-detail">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} data-testid="back-btn" className="p-2 rounded-full border border-border hover:border-primary"><ArrowLeft className="h-4 w-4" /></button>
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-display font-bold truncate">{c.name}</h2>
          <p className="text-xs text-muted-foreground">{adv?.company || adv?.name} · {c.start_date || "no start"} → {c.end_date || "no end"}</p>
        </div>
        <StatusPill s={c.status} />
      </div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setEditCamp(true)} data-testid="edit-campaign-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm hover:border-primary"><Pencil className="h-3.5 w-3.5" /> Edit Campaign</button>
        <button onClick={() => act("campaigns", c.id, "duplicate")} data-testid="duplicate-campaign-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm hover:border-primary"><Copy className="h-3.5 w-3.5" /> Duplicate</button>
        {c.status === "active"
          ? <button onClick={() => act("campaigns", c.id, "pause")} data-testid="pause-campaign-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm hover:border-primary"><Pause className="h-3.5 w-3.5" /> Pause</button>
          : <button onClick={() => act("campaigns", c.id, "resume")} data-testid="resume-campaign-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm hover:border-primary"><Play className="h-3.5 w-3.5" /> Resume</button>}
        {isAdmin && c.status === "pending" && <button onClick={() => act("campaigns", c.id, "approve")} data-testid="approve-campaign-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-primary text-primary text-sm"><Check className="h-3.5 w-3.5" /> Approve</button>}
        {isAdmin && c.status === "pending" && <button onClick={() => act("campaigns", c.id, "request_changes")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm"><RotateCcw className="h-3.5 w-3.5" /> Request Changes</button>}
        {isAdmin && c.status !== "rejected" && <button onClick={() => act("campaigns", c.id, "reject")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm text-destructive"><X className="h-3.5 w-3.5" /> Reject</button>}
        <button onClick={() => act("campaigns", c.id, "archive")} data-testid="archive-campaign-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm hover:border-primary"><Archive className="h-3.5 w-3.5" /> Archive</button>
        <button onClick={async () => { await act("campaigns", c.id, "delete"); onBack(); }} data-testid="delete-campaign-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm text-destructive"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
        <button onClick={() => setAdForm("new")} data-testid="create-ad-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"><Plus className="h-4 w-4" /> Create Ad</button>
      </div>
      {c.rejection_reason && c.status === "rejected" && <p className="text-sm text-destructive">Rejected: {c.rejection_reason}</p>}

      <section>
        <h3 className="text-lg font-display font-bold mb-3">Campaign Overall Analytics</h3>
        <CampaignAnalyticsBlock id={cid} />
      </section>

      <section>
        <h3 className="text-lg font-display font-bold mb-3">Ads ({ads.length})</h3>
        <div className="space-y-2">
          {ads.map((a) => (
            <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl border border-border surface-2 flex-wrap" data-testid={`ad-row-${a.id}`}>
              <MediaThumb url={a.media_url} type={a.ad_type} className="h-14 w-20 rounded shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{a.title}</p>
                <p className="text-xs text-muted-foreground capitalize">{a.ad_type} · {a.impressions || 0} imp · {a.completed_views || 0} views · {a.clicks || 0} clicks · CTR {a.ctr ?? 0}% · completion {a.completion_rate ?? 0}%</p>
                {a.status === "rejected" && a.rejection_reason && <p className="text-xs text-destructive mt-0.5">Rejected: {a.rejection_reason}</p>}
              </div>
              <StatusPill s={a.status} />
              <AdRowActions a={a} isAdmin={isAdmin} act={act}
                onEdit={(x) => setAdForm(x)} onPreview={(x) => setPreviewAd(x)}
                onAnalytics={(x) => onOpenAdAnalytics(x)} onDetail={(x) => setDetailAd(x)} />
            </div>
          ))}
          {!ads.length && <p className="text-muted-foreground text-center py-8 text-sm">No ads in this campaign yet. Click "Create Ad".</p>}
        </div>
      </section>

      {editCamp && <CampaignForm campaign={c} onClose={() => setEditCamp(false)} onSaved={() => { setEditCamp(false); load(); }} />}
      {adForm && <AdForm campaign={c} ad={adForm === "new" ? null : adForm} onClose={() => setAdForm(null)} onSaved={() => { setAdForm(null); load(); }} />}
      {previewAd && <AdPreviewModal ad={previewAd} onClose={() => setPreviewAd(null)} />}
      {detailAd && <AdDetailModal ad={detailAd} campaign={c} onClose={() => setDetailAd(null)} onEdit={() => { setAdForm(detailAd); setDetailAd(null); }} />}
    </div>
  );
}

export function AdAnalyticsView({ ad, onBack }) {
  return (
    <div className="space-y-4" data-testid="ad-analytics-view">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} data-testid="back-btn" className="p-2 rounded-full border border-border hover:border-primary"><ArrowLeft className="h-4 w-4" /></button>
        <MediaThumb url={ad.media_url} type={ad.ad_type} className="h-12 w-16 rounded" />
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-display font-bold truncate">{ad.title} — Analytics</h2>
          <p className="text-xs text-muted-foreground capitalize">{ad.ad_type} ad</p>
        </div>
        <StatusPill s={ad.status} />
      </div>
      <AdAnalyticsBlock id={ad.id} />
    </div>
  );
}

/* ---------- admin advertisers drill-down ---------- */
function AdvertiserDetailAdmin({ adv, onBack, onOpenCampaign, onAnalytics }) {
  const [camps, setCamps] = useState([]);
  const [fresh, setFresh] = useState(adv);
  const [campForm, setCampForm] = useState(null); // "new" | campaign
  const [editAdv, setEditAdv] = useState(false);
  const load = useCallback(() => {
    api.get(`/adx/advertisers/${adv.id}/campaigns`).then((r) => setCamps(r.data)).catch((e) => toast.error(apiError(e)));
    api.get(`/adx/advertisers/${adv.id}`).then((r) => setFresh(r.data)).catch(() => {});
  }, [adv.id]);
  useEffect(() => { load(); }, [load]);
  const act = async (kind, id, action) => {
    try { if (await entityAction(kind, id, action)) { toast.success("Updated"); load(); } }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-6" data-testid="advertiser-detail">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} data-testid="back-btn" className="p-2 rounded-full border border-border hover:border-primary"><ArrowLeft className="h-4 w-4" /></button>
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-display font-bold truncate">{fresh.company || fresh.name}</h2>
          <p className="text-xs text-muted-foreground">{fresh.contact} · {fresh.email} · {fresh.phone || "no phone"} {fresh.website ? `· ${fresh.website}` : ""}</p>
        </div>
        <StatusPill s={fresh.status} />
        <span className={`text-xs font-semibold ${fresh.paid ? "text-primary" : "text-amber-400"}`} data-testid="paid-badge">{fresh.paid ? "Paid" : "Unpaid"}</span>
      </div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setEditAdv(true)} data-testid="edit-advertiser-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm hover:border-primary"><Pencil className="h-3.5 w-3.5" /> Edit</button>
        <button onClick={onAnalytics} data-testid="advertiser-analytics-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm hover:border-primary"><BarChart3 className="h-3.5 w-3.5" /> Analytics</button>
        <button onClick={() => setCampForm("new")} data-testid="new-campaign-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"><Plus className="h-4 w-4" /> New Campaign</button>
        {!fresh.paid && <button onClick={() => act("advertisers", fresh.id, "mark_paid")} data-testid="mark-paid-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm hover:border-primary"><CreditCard className="h-3.5 w-3.5" /> Mark Paid</button>}
        {fresh.status === "active"
          ? <button onClick={() => act("advertisers", fresh.id, "pause")} data-testid="pause-advertiser-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm hover:border-primary"><Pause className="h-3.5 w-3.5" /> Pause</button>
          : <button onClick={() => act("advertisers", fresh.id, "activate")} data-testid="activate-advertiser-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-primary text-primary text-sm"><Play className="h-3.5 w-3.5" /> Activate</button>}
        <button onClick={() => act("advertisers", fresh.id, "archive")} data-testid="archive-advertiser-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm hover:border-primary"><Archive className="h-3.5 w-3.5" /> Archive</button>
        <button onClick={async () => { await act("advertisers", fresh.id, "delete"); onBack(); }} data-testid="delete-advertiser-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm text-destructive"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
      </div>

      <section>
        <h3 className="text-lg font-display font-bold mb-3">Campaigns ({camps.length})</h3>
        <div className="space-y-2">
          {camps.map((c) => (
            <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl border border-border surface-2 flex-wrap" data-testid={`campaign-row-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{c.name}</p>
                <p className="text-xs text-muted-foreground">{c.ad_count} ads · {c.impressions} imp · {c.clicks} clicks · {c.start_date || "no start"} → {c.end_date || "no end"}</p>
              </div>
              <StatusPill s={c.status} />
              <CampaignRowActions c={c} isAdmin act={act} onEdit={(x) => setCampForm(x)} onAnalytics={(x) => onOpenCampaign(x)} onOpen={(x) => onOpenCampaign(x)} />
            </div>
          ))}
          {!camps.length && <p className="text-muted-foreground text-center py-8 text-sm">No campaigns yet. Click "New Campaign".</p>}
        </div>
      </section>

      {campForm && <CampaignForm advertiserId={adv.id} campaign={campForm === "new" ? null : campForm} onClose={() => setCampForm(null)} onSaved={() => { setCampForm(null); load(); }} />}
      {editAdv && <AdvertiserForm advertiser={fresh} onClose={() => setEditAdv(false)} onSaved={() => { setEditAdv(false); load(); }} />}
    </div>
  );
}

function AdvertisersAdmin() {
  const [stack, setStack] = useState([{ name: "list" }]);
  const push = (v) => setStack((s) => [...s, v]);
  const pop = () => setStack((s) => s.slice(0, -1));
  const top = stack[stack.length - 1];

  if (top.name === "advertiser")
    return <AdvertiserDetailAdmin adv={top.adv} onBack={pop}
      onOpenCampaign={(c) => push({ name: "campaign", camp: c })}
      onAnalytics={() => push({ name: "advAnalytics", adv: top.adv })} />;
  if (top.name === "advAnalytics")
    return (
      <div className="space-y-4" data-testid="advertiser-analytics-view">
        <div className="flex items-center gap-3">
          <button onClick={pop} data-testid="back-btn" className="p-2 rounded-full border border-border hover:border-primary"><ArrowLeft className="h-4 w-4" /></button>
          <h2 className="text-2xl font-display font-bold">{top.adv.company || top.adv.name} — Advertiser Analytics</h2>
        </div>
        <AdvertiserAnalyticsBlock id={top.adv.id} />
      </div>
    );
  if (top.name === "campaign")
    return <CampaignDetailView cid={top.camp.id} isAdmin onBack={pop} onOpenAdAnalytics={(a) => push({ name: "adAnalytics", ad: a })} />;
  if (top.name === "adAnalytics")
    return <AdAnalyticsView ad={top.ad} onBack={pop} />;
  return <AdvertiserListAdmin onView={(a) => push({ name: "advertiser", adv: a })} onAnalytics={(a) => push({ name: "advAnalytics", adv: a })} />;
}

function AdvertiserListAdmin({ onView, onAnalytics }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null); // "new" | advertiser
  const [campFor, setCampFor] = useState(null);
  const load = useCallback(() => {
    api.get("/adx/advertisers").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action) => {
    try { if (await entityAction("advertisers", id, action)) { toast.success("Updated"); load(); } }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="advertisers-list">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-lg font-display font-bold">Advertisers ({rows.length})</h3>
        <button onClick={() => setForm("new")} data-testid="new-advertiser-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold"><Plus className="h-4 w-4" /> New Advertiser</button>
      </div>
      <div className="space-y-2">
        {rows.map((a) => (
          <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl border border-border surface-2 flex-wrap" data-testid={`advertiser-row-${a.id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-medium truncate">{a.company || a.name}</p>
              <p className="text-xs text-muted-foreground">{a.contact} · {a.email} · {a.phone || "—"}</p>
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">{a.campaign_count} campaigns · {a.ad_count} ads</span>
            <StatusPill s={a.status} />
            <span className={`text-[11px] font-semibold ${a.paid ? "text-primary" : "text-amber-400"}`}>{a.paid ? "Paid" : "Unpaid"}</span>
            <div className="flex gap-1 flex-wrap">
              <IconBtn title="View" testid="advertiser-view" onClick={() => onView(a)}><Eye className="h-3.5 w-3.5" /></IconBtn>
              <IconBtn title="Edit" testid="advertiser-edit" onClick={() => setForm(a)}><Pencil className="h-3.5 w-3.5" /></IconBtn>
              <IconBtn title="Create Campaign" testid="advertiser-create-campaign" onClick={() => setCampFor(a)}><Plus className="h-3.5 w-3.5" /></IconBtn>
              <IconBtn title="Analytics" testid="advertiser-analytics" onClick={() => onAnalytics(a)}><BarChart3 className="h-3.5 w-3.5" /></IconBtn>
              {a.status === "active"
                ? <IconBtn title="Pause" testid="advertiser-pause" onClick={() => act(a.id, "pause")}><Pause className="h-3.5 w-3.5" /></IconBtn>
                : <IconBtn title="Activate" testid="advertiser-activate" onClick={() => act(a.id, "activate")}><Play className="h-3.5 w-3.5 text-primary" /></IconBtn>}
              <IconBtn title="Archive" testid="advertiser-archive" onClick={() => act(a.id, "archive")}><Archive className="h-3.5 w-3.5" /></IconBtn>
              <IconBtn title="Delete" testid="advertiser-delete" danger onClick={() => act(a.id, "delete")}><Trash2 className="h-3.5 w-3.5" /></IconBtn>
            </div>
          </div>
        ))}
        {!rows.length && <p className="text-muted-foreground text-center py-10 text-sm">No advertisers yet. Click "New Advertiser".</p>}
      </div>
      {form && <AdvertiserForm advertiser={form === "new" ? null : form} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
      {campFor && <CampaignForm advertiserId={campFor.id} onClose={() => setCampFor(null)} onSaved={() => { setCampFor(null); load(); toast.success("Campaign created — open the advertiser to manage it"); }} />}
    </div>
  );
}

/* ---------- test ads (improved) ---------- */
function TestAdsPanel() {
  const [ads, setAds] = useState([]);
  const [f, setF] = useState({ title: "", brand_name: "Demo Brand", description: "", cta_text: "Learn More", destination_url: "", ad_type: "image", media_url: "" });
  const [edit, setEdit] = useState(null);
  const [preview, setPreview] = useState(null);
  const load = useCallback(() => api.get("/admin/test-ads").then((r) => setAds(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!f.title || !f.media_url) return toast.error("Title and media required");
    try { await api.post("/admin/test-ads", f); toast.success("Test ad created & active"); setF({ ...f, title: "", media_url: "", description: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (a) => { await api.patch(`/admin/test-ads/${a.id}`, { status: a.status === "active" ? "paused" : "active" }); load(); };
  const del = async (a) => { if (!window.confirm(`Delete test ad "${a.title}"?`)) return; await api.patch(`/admin/test-ads/${a.id}`, { delete: true }); load(); };
  const saveEdit = async () => {
    try { await api.patch(`/admin/test-ads/${edit.id}`, edit); toast.success("Test ad updated"); setEdit(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border surface-2 p-6 space-y-3">
        <h3 className="text-lg font-display font-bold">Create Test Ad</h3>
        <p className="text-sm text-muted-foreground">Upload your own image/video to test the full chapter-unlock flow — no real advertiser needed.</p>
        <Inp placeholder="Ad title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="testad-title" />
        <div className="flex gap-3"><Inp placeholder="Brand name" value={f.brand_name} onChange={(e) => setF({ ...f, brand_name: e.target.value })} /><Inp placeholder="CTA text" value={f.cta_text} onChange={(e) => setF({ ...f, cta_text: e.target.value })} /></div>
        <Inp placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <Inp placeholder="Destination URL (optional)" value={f.destination_url} onChange={(e) => setF({ ...f, destination_url: e.target.value })} />
        <div className="flex gap-2">{["image", "video"].map((t) => <button key={t} onClick={() => setF({ ...f, ad_type: t, media_url: "" })} className={`px-4 py-2 rounded-full text-sm border ${f.ad_type === t ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{t}</button>)}</div>
        <AdMediaField type={f.ad_type} value={f.media_url} onChange={(url, t) => setF((p) => ({ ...p, media_url: url, ad_type: t || p.ad_type }))}
            onMeta={(m) => setF((p) => ({ ...p, title: p.title || m.title || "", description: p.description || m.description || "", ...("headline" in p ? { headline: p.headline || m.title || "" } : {}), destination_url: p.destination_url || m.destination_url || "" }))} />
        <button onClick={create} data-testid="create-testad-btn" className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">Create & Activate Test Ad</button>
      </div>
      <div className="space-y-2">
        {ads.map((a) => (
          <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl border border-border surface-2 flex-wrap" data-testid={`testad-row-${a.id}`}>
            <MediaThumb url={a.media_url} type={a.ad_type} className="h-12 w-16 rounded shrink-0" />
            <div className="flex-1 min-w-0"><p className="font-medium truncate">{a.title}</p><p className="text-xs text-muted-foreground">{a.brand_name} · {a.ad_type}</p></div>
            <StatusPill s={a.status} />
            <div className="flex gap-1">
              <IconBtn title="Preview" testid="testad-preview" onClick={() => setPreview(a)}><Eye className="h-3.5 w-3.5" /></IconBtn>
              <IconBtn title="Edit" testid="testad-edit" onClick={() => setEdit({ ...a })}><Pencil className="h-3.5 w-3.5" /></IconBtn>
              <button onClick={() => toggle(a)} data-testid="testad-toggle" className="text-xs px-3 py-1.5 rounded border border-border hover:border-primary">{a.status === "active" ? "Pause" : "Activate"}</button>
              <IconBtn title="Delete" testid="testad-delete" danger onClick={() => del(a)}><Trash2 className="h-3.5 w-3.5" /></IconBtn>
            </div>
          </div>
        ))}
        {!ads.length && <p className="text-muted-foreground text-center py-6 text-sm">No test ads yet.</p>}
      </div>
      {preview && <AdPreviewModal ad={preview} onClose={() => setPreview(null)} />}
      {edit && (
        <Modal title={`Edit Test Ad — ${edit.title}`} onClose={() => setEdit(null)} wide>
          <div className="space-y-3">
            <Inp placeholder="Ad title" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="testad-edit-title" />
            <div className="flex gap-3"><Inp placeholder="Brand name" value={edit.brand_name || ""} onChange={(e) => setEdit({ ...edit, brand_name: e.target.value })} /><Inp placeholder="CTA text" value={edit.cta_text || ""} onChange={(e) => setEdit({ ...edit, cta_text: e.target.value })} /></div>
            <Inp placeholder="Description" value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
            <Inp placeholder="Destination URL" value={edit.destination_url || ""} onChange={(e) => setEdit({ ...edit, destination_url: e.target.value })} />
            <div className="flex gap-2">{["image", "video"].map((t) => <button key={t} onClick={() => setEdit({ ...edit, ad_type: t, media_url: "" })} className={`px-4 py-2 rounded-full text-sm border ${edit.ad_type === t ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{t}</button>)}</div>
            <AdMediaField type={edit.ad_type} value={edit.media_url} onChange={(url, t) => setEdit((p) => ({ ...p, media_url: url, ad_type: t || p.ad_type }))} />
            <button onClick={saveEdit} data-testid="testad-save" className="w-full px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">Save Changes</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ---------- ad settings (moved, unchanged behavior) ---------- */
function AdSettingsPanel() {
  const [ads, setAds] = useState(null);
  const [stats, setStats] = useState(null);
  const [saved, setSaved] = useState("");
  const t = useRef(null);
  useEffect(() => {
    api.get("/admin/ads").then((r) => setAds(r.data));
    api.get("/admin/ad-analytics").then((r) => setStats(r.data));
  }, []);
  const upd = (patch) => {
    const n = { ...ads, ...patch };
    setAds(n);
    setSaved("saving");
    clearTimeout(t.current);
    t.current = setTimeout(async () => {
      try {
        await api.put("/admin/ads", { enabled: n.enabled, interval: parseInt(n.interval) || 5, countdown: parseInt(n.countdown) || 10, short_video_policy: n.short_video_policy || "loop", provider: n.provider, ad_source: n.ad_source, placement: n.placement, daily_limit: parseInt(n.daily_limit) || 30, premium_bypass: n.premium_bypass, test_mode: n.test_mode, fallback_message: n.fallback_message, advertiser_fee: parseFloat(n.advertiser_fee) || 29 });
        setSaved("saved");
        setTimeout(() => setSaved(""), 2000);
      } catch (e) { setSaved(""); toast.error(apiError(e)); }
    }, 900);
  };
  if (!ads) return null;
  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[["Campaigns", stats.campaigns_total], ["Active", stats.active], ["Pending", stats.pending], ["Impressions", stats.impressions]].map(([l, v]) => (
            <div key={l} className="rounded-2xl border border-border surface-2 p-4"><p className="track-label text-xs text-muted-foreground">{l}</p><p className="text-2xl font-display font-bold mt-1">{v}</p></div>
          ))}
        </div>
      )}
      <section className="rounded-2xl border border-border surface-2 p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-display font-bold flex items-center gap-3">Ad System {saved === "saving" && <span className="text-xs text-muted-foreground">Saving…</span>}{saved === "saved" && <span className="text-xs text-primary inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" />Saved</span>}</h3>
          <label className="flex items-center gap-2 font-medium"><input type="checkbox" checked={!!ads.enabled} onChange={(e) => upd({ enabled: e.target.checked })} data-testid="ads-enabled" /> {ads.enabled ? "Enabled" : "Disabled"}</label>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div><p className="track-label text-xs text-muted-foreground mb-2">Ad Source</p>
            <Sel value={ads.ad_source || "test"} onChange={(e) => upd({ ad_source: e.target.value })} data-testid="ads-source">
              <option value="test">Test Ads (admin uploads)</option><option value="direct">Direct Advertiser Campaigns</option><option value="external">External Ad Network (future)</option>
            </Sel></div>
          <div><p className="track-label text-xs text-muted-foreground mb-2">External Provider (when source = external)</p>
            <Sel value={ads.provider || "simulated"} onChange={(e) => upd({ provider: e.target.value })}>
              <option value="simulated">Simulated</option><option value="google-admob">Google AdMob</option><option value="unity">Unity Ads</option><option value="applovin">AppLovin</option>
            </Sel></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div><p className="text-xs text-muted-foreground mb-1">Free chapters before ads</p><Inp type="number" value={ads.interval ?? 5} onChange={(e) => upd({ interval: e.target.value })} data-testid="ads-interval" /></div>
          <div><p className="text-xs text-muted-foreground mb-1">Rewarded ad length (s)</p><Inp type="number" value={ads.countdown ?? 10} onChange={(e) => upd({ countdown: e.target.value })} data-testid="ads-countdown-input" /></div>
          <div><p className="text-xs text-muted-foreground mb-1">If video is shorter</p>
            <select value={ads.short_video_policy || "loop"} onChange={(e) => upd({ short_video_policy: e.target.value })} data-testid="ads-short-video-policy"
              className="w-full bg-background border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-primary">
              <option value="loop">Replay until full ad length is watched</option>
              <option value="full_length">Unlock after the whole (shorter) video</option>
            </select></div>
          <div><p className="text-xs text-muted-foreground mb-1">Daily ad limit per reader</p><Inp type="number" value={ads.daily_limit ?? 30} onChange={(e) => upd({ daily_limit: e.target.value })} data-testid="ads-daily-limit" /></div>
          <div><p className="text-xs text-muted-foreground mb-1">Advertiser activation fee ($)</p><Inp type="number" value={ads.advertiser_fee ?? 29} onChange={(e) => upd({ advertiser_fee: e.target.value })} data-testid="ads-advertiser-fee" /></div>
        </div>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ads.premium_bypass !== false} onChange={(e) => upd({ premium_bypass: e.target.checked })} /> Premium bypass</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ads.test_mode !== false} onChange={(e) => upd({ test_mode: e.target.checked })} data-testid="ads-test-mode" /> Test mode</label>
        </div>
        <div><p className="track-label text-xs text-muted-foreground mb-2">Fallback message (ad fails to load)</p><Inp value={ads.fallback_message || ""} onChange={(e) => upd({ fallback_message: e.target.value })} /></div>
        <div className="rounded-xl border border-border p-4 bg-background text-sm text-muted-foreground">Chapters 1–{ads.interval ?? 5} free · every chapter after needs a rewarded ad · max {ads.daily_limit ?? 30} ads/reader/day (auto-resets daily) · Premium {ads.premium_bypass !== false ? "ad-free" : "sees ads"}.</div>
      </section>
    </div>
  );
}

/* ---------- root: admin Ads tab ---------- */
export default function AdCMS() {
  const [sub, setSub] = useState("Advertisers");
  const SUBS = ["Advertisers", "Test Ads", "Media Library", "Settings"];
  return (
    <div data-testid="ad-cms">
      <div className="flex gap-2 flex-wrap mb-6">
        {SUBS.map((s) => (
          <button key={s} onClick={() => setSub(s)} data-testid={`ads-sub-${s.toLowerCase().replace(/ /g, "-")}`}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${sub === s ? "bg-primary text-primary-foreground" : "border border-border hover:border-primary"}`}>{s}</button>
        ))}
      </div>
      {sub === "Advertisers" && <AdvertisersAdmin />}
      {sub === "Test Ads" && <TestAdsPanel />}
      {sub === "Media Library" && <MediaLibrary />}
      {sub === "Settings" && <AdSettingsPanel />}
    </div>
  );
}
